import { reactive } from "vue";

export interface USAddressFormData {
  address1: string;
  address2: string;
  city: string;
  state: string | null;
  zip: string;
}

/**
 * Reads the value from an input event payload.
 * @param payload - The input event payload.
 * @returns The string value of the input.
 */
function readInputValue(payload: unknown): string {
  if (payload && typeof payload === "object" && "value" in payload) {
    const value = (payload as { value: unknown }).value;
    return typeof value === "string" ? value : String(value ?? "");
  }
  return "";
}

/**
 * Manages the form data model for a US address.
 *
 * Note: this is internal UI data state, not just the US "state" (AL, CA, etc.) field.
 * @returns An object containing form data and field event handlers.
 */
export function useUSAddressFormData() {
  const formData = reactive<USAddressFormData>({
    address1: "",
    address2: "",
    city: "",
    state: null,
    zip: "",
  });

  const onAddress1Input = (...args: unknown[]) => {
    formData.address1 = readInputValue(args[0]);
  };

  const onAddress2Input = (...args: unknown[]) => {
    formData.address2 = readInputValue(args[0]);
  };

  const onCityInput = (...args: unknown[]) => {
    formData.city = readInputValue(args[0]);
  };

  const onStateChange = (event: { value: string | null }) => {
    formData.state = event.value;
  };

  const resetFormData = () => {
    formData.address1 = "";
    formData.address2 = "";
    formData.city = "";
    formData.state = null;
    formData.zip = "";
  };

  return {
    formData,
    onAddress1Input,
    onAddress2Input,
    onCityInput,
    onStateChange,
    resetFormData,
  };
}
